<template>
  <div class="stc-member-traits">
    <div class="stc-member-traits__price-container">
      <ProductPrice :id="productId" :product="product" />
    </div>

    <div v-if="hasAttributes" class="stc-member-traits__attributes">
      <ColorSwatches v-if="showColorSwatches && !isConsolidatedThirdVariantFlag" :id="productId" :color-props="colorProps" />
      <ProductSizes v-if="hasSizes" :id="productId" :product="product" />
      <ProductOrientation
        v-if="hasTypes && !isSplitThirdVariantFlag && !isConsolidatedThirdVariantFlag && !isSplitThirdVariantMember"
        class="stc-member-traits__orientation"
      />
    </div>

    <div v-if="showQuantitySelector" class="stc-member-traits__quantity-wrapper">
      <span class="margin-right-xxs">Qty:</span>
      <QuantitySelector v-model="internalQuantity" :min="minQuantity" :max="maxQuantity" size="small" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, watch } from 'vue';
import { useProductFlags } from '~/modules/featureControl/composables/useProductFlags';
import { useBrand } from '~/composables/useBrand';
import useColor from '~/modules/color/composables/useColor';
import useUpc from '~/modules/upc/composables/useUpc';
import { useCollectionSelectionStore } from '~/stores/useCollectionSelectionStore';

import ProductSizes from '~/modules/size/ProductSize.vue';
import ProductOrientation from '~/modules/type/components/ProductOrientation.vue';
import ColorSwatches from '~/modules/color/ColorSwatches.vue';
import QuantitySelector from '@atomic-ui/quantitySelector/QuantitySelector';
import ProductPrice from '~/modules/price/components';

const props = withDefaults(
  defineProps<{
    productId: ProductId;
    product: Product;
    showQuantitySelector?: boolean;
    quantity?: number;
    minQuantity?: number;
  }>(),
  {
    showQuantitySelector: false,
    quantity: 1,
    minQuantity: 1,
  },
);

const emit = defineEmits(['update:quantity']);

const { isMcom } = useBrand();
const { isSplitThirdVariantFlag, isConsolidatedThirdVariantFlag, hasColorSwatchesFlag, hasColorsFlag } = useProductFlags();
const { colorProps } = useColor();
const { selectedUpcId } = useUpc();
const selectionStore = useCollectionSelectionStore();

const isSplitThirdVariantMember = computed(() => !!props.product?.detail?.flags?.isSplitThirdVariant);
const showColorSwatches = computed(() => (isMcom ? hasColorSwatchesFlag : hasColorsFlag));

const hasSizes = computed(() => !!props.product?.traits?.sizes?.sizeMap);
const hasTypes = computed(() => !!props.product?.traits?.types?.typeMap);

const hasAttributes = computed(() => {
  return showColorSwatches.value || hasSizes.value || hasTypes.value;
});

const maxQuantity = computed(() => props.product?.detail?.maxQuantity || 10);

const internalQuantity = computed({
  get: () => props.quantity,
  set: (val) => emit('update:quantity', val),
});

watch(
  () => (selectedUpcId.value ? selectionStore.getQuantityForUpc(props.productId, selectedUpcId.value) : 0),
  (newQty) => {
    emit('update:quantity', newQty || 1);
  },
  { immediate: true },
);
</script>

<style lang="scss" scoped>
@import '~settingSCSS';

.stc-member-traits {
  display: flex;
  flex-direction: column;
  gap: 16px;

  @include breakpoint(medium up) {
    gap: 24px;
  }

  .stc-member-traits__price-container {
    &:deep(.price-layout) {
      @include margin-bottom-z;
    }
  }

  .stc-member-traits__attributes {
    display: flex;
    flex-direction: column;
    gap: 16px;

    &:deep(.size-chips),
    &:deep(.stc-member-traits__orientation),
    &:deep(.price-layout) {
      @include margin-bottom-z;
    }

    &:deep(.colors) {
      @include padding-bottom-z;
    }

    &:deep(.size-split-variant-wrapper) {
      margin-bottom: -16px;

      .size-chips {
        @include margin-bottom-s;
      }

      @include breakpoint(medium up) {
        margin-bottom: -24px;
      }
    }

    @include breakpoint(medium up) {
      gap: 24px;
    }
  }

  // Hide unwanted elements from child components
  :deep(.order-swatches-button),
  :deep(.buttons-container),
  :deep(.size-chips .link-sm),
  :deep(.findation-container) {
    display: none !important;
  }

  :deep(.expandable__button i) {
    @include margin-left-xxs;
  }

  .stc-member-traits__quantity-wrapper {
    display: flex;
    align-items: center;
    @include margin-top-xxs;
  }
}
</style>
